import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listenToParentMessages, isInIframe } from '@/Utils/iframe-utils';
import { ThemeContextType } from '@/Models';
import { setTheme as setReduxTheme } from '@/Redux/slices/iframeParamsSlice';
import type { RootState } from '@/Redux/reduxStore';
import { 
  initializeParams, 
  refreshParamsIfChanged, 
  checkAndRecoverParams,
  type ThemeMode, 
  type BrandType 
} from '@/Utils/params-manager';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const THEME_STORAGE_KEY = 'app-theme-mode';

export const ThemeContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();
  const iframeParams = useSelector((state: RootState) => state.iframeParams);
  
  // Initialize from Redux store or environment/URL params
  const [isInitializing, setIsInitializing] = useState(true);

  // Get brand from environment variable first
  const envBrand: BrandType = (import.meta.env.VITE_BRAND as BrandType) || 'standard';
  const isLFBrand = envBrand === 'lf';
  
  const [mode, setMode] = useState<ThemeMode>(() => {
    // LF brand is ALWAYS dark mode
    if (isLFBrand) {
      return 'dark';
    }
    
    // If in iframe, read theme from URL params (standard brand only)
    if (isInIframe()) {
      const params = new URLSearchParams(window.location.search);
      const themeParam = params.get('theme');
      if (themeParam === 'dark' || themeParam === 'light') {
        return themeParam;
      }
      return 'light'; // Default for standard brand
    }
    
    // For standard brand, use localStorage or default to light
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return (stored === 'dark' || stored === 'light') ? stored : 'light';
  });

  const tenantId = iframeParams.isInitialized ? iframeParams.tenantId : null;
  const brand: BrandType = tenantId === '4'
    ? 'africabet'
    : iframeParams.isInitialized
    ? iframeParams.brand
    : envBrand;
  const clientId = iframeParams.isInitialized ? iframeParams.clientId : null;
  const currency = iframeParams.isInitialized ? iframeParams.currency : null;
  const premiumSourceUrl = iframeParams.isInitialized ? iframeParams.premiumSourceUrl : null;

  // Helper function to read and initialize params from URL
  const initializeParamsFromUrl = () => {
    const iframeData = initializeParams(envBrand, isLFBrand);
    setMode(iframeData.theme);
  };

  // Initialize iframe params from URL and store in Redux
  useEffect(() => {
    if (!iframeParams.isInitialized && isInIframe()) {
      initializeParamsFromUrl();
    }
    setIsInitializing(false);
  }, [dispatch, iframeParams.isInitialized, envBrand, isLFBrand]);

  // Detect URL param changes and refresh when clientId/tenantId changes
  useEffect(() => {
    if (!isInIframe() || !iframeParams.isInitialized) return;

    const refreshedParams = refreshParamsIfChanged(
      envBrand,
      isLFBrand,
      iframeParams.clientId,
      iframeParams.tenantId,
      iframeParams.currency,
      iframeParams.premiumSourceUrl
    );

    if (refreshedParams) {
      setMode(refreshedParams.theme);
    }
  }, [window.location.search, iframeParams.clientId, iframeParams.tenantId, iframeParams.currency, iframeParams.premiumSourceUrl, iframeParams.isInitialized, envBrand, isLFBrand, dispatch]);

  // Check for stale params on page visibility change (e.g., user returns to tab)
  useEffect(() => {
    if (!isInIframe() || !iframeParams.isInitialized) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndRecoverParams(iframeParams.clientId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [iframeParams.isInitialized, iframeParams.clientId]);

  useEffect(() => {
    let isSubscribed = true;

    // Listen for theme changes from parent (standard brand only)
    const cleanup = listenToParentMessages((message) => {
      if (!isSubscribed) return;
      
      // Handle theme changes (standard brand only)
      if (message.type === 'SET_THEME' && message.payload?.theme && !isLFBrand) {
        const { theme } = message.payload;
        if (theme === 'light' || theme === 'dark') {
          setMode(theme);
          dispatch(setReduxTheme(theme));
        }
      }
    });

    return () => {
      isSubscribed = false;
      cleanup();
    };
  }, [isLFBrand, dispatch]);

  useEffect(() => {
    // LF brand is always dark mode
    if (isLFBrand && mode !== 'dark') {
      setMode('dark');
      dispatch(setReduxTheme('dark'));
    }
    
    // Only save to localStorage if not in iframe and not LF brand
    if (!isInIframe() && !isLFBrand) {
      localStorage.setItem(THEME_STORAGE_KEY, mode);
    }
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode, isLFBrand, dispatch]);

  const toggleTheme = () => {
    // Prevent theme toggle for LF brand
    if (isLFBrand) {
      return;
    }
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    dispatch(setReduxTheme(newMode));
  };

  const setTheme = (newMode: ThemeMode) => {
    // Prevent setting light mode for LF brand
    if (isLFBrand && newMode !== 'dark') {
      return;
    }
    setMode(newMode);
    dispatch(setReduxTheme(newMode));
  };

  // Don't render children until initialization is complete
  if (isInitializing) {
    return null;
  }

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme, setTheme, brand, tenantId, clientId, currency, premiumSourceUrl }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
};
